import React, { useEffect, useState, useRef, useCallback } from "react";
import "./Mid-Section-Pc.scss";

//imports for user data
import { useSelector, useDispatch } from "react-redux";

//import icons
import { IoChatbubbles } from "react-icons/io5";
import { TbDeviceMobileMessage } from "react-icons/tb";
import OnlineChatList from "./OnlineChatList";
import OnlineChatMessages from "./OnlineChatMessages";
import MobileChatMessages from "./MobileChatMessages"; // New component for mobile messages

// Import the utility functions
import {
  formatTimestamp,
  formatDate,
  getLastMessageSnippet,
  getLastMessageTimestamp,
  storeConversation,
  getStoredConversations,
  storeConversationsFromAuth, // Add this import
  getStoredConversationsFull, // Add this import
} from "./utils/ChatUtils";

const MessagingApp = () => {
  const [messageInput, setMessageInput] = useState("");
  const [activeChat, setActiveChat] = useState("");
  const [activeChatId, setActiveChatId] = useState(""); // For storing the active chat's user ID
  const [activeChatType, setActiveChatType] = useState("online"); // "online" or "mobile"
  const [mobileRecipients, setMobileRecipients] = useState([]); // For storing mobile message recipients
  const [mobileMessageText, setMobileMessageText] = useState(""); // For mobile broadcast message

  const { user, properties, message, profile } = useSelector(
    (state) => state.landlordslice
  );

  //web socket functionality
  const socketRef = useRef(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const maxReconnectAttempts = 5;
  const reconnectTimeouts = [1000, 2000, 4000, 8000, 16000]; // Exponential backoff

  // Create chats from profile's role takers
  const createRoleTakerChats = () => {
    // Check if profile exists and has role_takers
    if (!profile?.role_takers || profile.role_takers.length === 0) {
      return [
        {
          groupName: "Support Team",
          users: [
            {
              user: "No contacts available",
              isPlaceholder: true,
              messages: [],
            },
          ],
        },
      ];
    }

    // Transform role takers into chat contacts
    const roleTakerChats = {
      groupName: "Support Team",
      users: profile.role_takers.map((roleTaker) => ({
        user: roleTaker.name || `Staff ${roleTaker._id.substring(0, 5)}`,
        displayName: `${roleTaker.name || "Staff"} (${
          roleTaker.role || "Support"
        })`,
        userId: roleTaker._id,
        role: roleTaker.role || "Support",
        profilePicture:
          roleTaker.image && roleTaker.image.length > 0
            ? roleTaker.image[0]
            : "",
        messages: [
          {
            text: `Hello! I'm a ${
              roleTaker.role || "support staff"
            } for your property.`,
            type: "incoming",
            timestamp: new Date().toISOString(),
          },
        ],
      })),
    };

    // If there's a caretaker in tenant_details, add them too
    if (profile?.tenant_details?.caretaker?.name) {
      const caretaker = profile.tenant_details.caretaker;
      roleTakerChats.users.push({
        user: caretaker.name || "Caretaker",
        displayName: `${caretaker.name || "Caretaker"} (Caretaker)`,
        userId: `caretaker-${Date.now()}`, // Generate a temporary ID if not available
        role: "Caretaker",
        profilePicture: caretaker.profile_picture || "",
        messages: [
          {
            text: "Hello! I'm the caretaker for your property.",
            type: "incoming",
            timestamp: new Date().toISOString(),
          },
        ],
      });
    }

    return [roleTakerChats];
  };

  const [chats, setChats] = useState([]);

  // Initialize chats when profile data is available
  useEffect(() => {
    if (profile) {
      setChats(createRoleTakerChats());
    }
  }, [profile]);

  useEffect(() => {
    console.log("Chats updated:", chats);
  }, [chats]);

  // Initialize conversations state with stored values from localStorage
  const [conversations, setConversations] = useState(() =>
    getStoredConversations()
  );

  // Reconnect function with exponential backoff
  const reconnectWebSocket = useCallback(() => {
    if (reconnectAttempts >= maxReconnectAttempts) {
      setIsError(true);
      setErrorMessage("Failed to reconnect after multiple attempts");
      return;
    }

    const timeout = reconnectTimeouts[reconnectAttempts] || 16000;
    setTimeout(() => {
      connectWebSocket();
      setReconnectAttempts((prev) => prev + 1);
    }, timeout);
  }, [reconnectAttempts]);

  // Connect to WebSocket
  const connectWebSocket = useCallback(() => {
    setIsLoading(true);

    const webSocketUrl = process.env.REACT_APP_WEB_SOCKET_ENDPOINT;
    console.log("Connecting to WebSocket:", webSocketUrl);
    const socket = new WebSocket(webSocketUrl);
    socketRef.current = socket;

    socket.onopen = () => {
      console.log("WebSocket connected");
      setIsLoading(false);
      setIsError(false);
      setReconnectAttempts(0); // Reset reconnect attempts on successful connection

      // Always authenticate after connection is established
      authenticateWebSocket();
    };

    socket.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        console.log("Message received:", msg);

        handleWebSocketMessage(msg);
      } catch (error) {
        console.error("Error parsing WebSocket message:", error);
      }
    };

    socket.onclose = (event) => {
      console.log("WebSocket disconnected:", event.reason);
      setIsAuthenticated(false);
      setIsLoading(false);

      // Attempt to reconnect
      // reconnectWebSocket();
    };

    socket.onerror = (error) => {
      console.error("WebSocket error:", error);
      setIsError(true);
      setErrorMessage("Connection error occurred");
      setIsLoading(false);

      // Close the socket to ensure onclose is triggered
      socket.close();
    };
  }, [reconnectWebSocket]);

  // Authentication function
  const authenticateWebSocket = () => {
    if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
      console.log("Cannot authenticate: WebSocket is not open");
      return;
    }

    const userId = user?.data?.tenantId;
    if (!userId) {
      console.error("Cannot authenticate: No user ID available");
      return;
    }

    const authPayload = {
      msg_type: "user.auth",
      msg_res: {
        message: "Authenticating WebSocket",
        data: {
          user_id: userId,
        },
      },
    };

    socketRef.current.send(JSON.stringify(authPayload));
  };

  // Handle incoming WebSocket messages
  const handleWebSocketMessage = (msg) => {
    const { msg_type, msg_res } = msg;

    switch (msg_type) {
      case "user.auth.ack":
        setIsAuthenticated(true);
        setIsSuccess(true);
        console.log("Authentication successful");

        // Store all conversations from the auth response
        storeConversationsFromAuth(msg);

        // Update the conversations state with the mapping
        if (msg?.msg_res?.data?.conversations) {
          const conversationMap = {};
          msg.msg_res.data.conversations.forEach((conv) => {
            if (conv.other_user_id && conv.conversation_id) {
              conversationMap[conv.other_user_id] = conv.conversation_id;
            }
          });
          setConversations(conversationMap);
        }
        break;

      case "user.message.received":
        handleNewMessage(msg_res.data);
        break;

      case "user.message.sent.ack":
        handleMessageSentAck(msg_res.data);
        break;

      case "user.message.broadcast.ack":
        handleBroadcastMessageAck(msg_res.data);
        break;

      case "conversation.history.response":
        handleConversationHistory(msg_res.data);
        break;

      case "error":
        handleError(msg_res);
        break;

      default:
        console.log("Unhandled message type:", msg_type);
    }
  };

  // Handle new incoming messages
  const handleNewMessage = (data) => {
    const { sender_id, content, conversation_id, createdAt } = data;

    // Store the conversation ID for this sender (in state and localStorage)
    setConversations((prev) => {
      const updated = { ...prev, [sender_id]: conversation_id };
      // Store in localStorage
      storeConversation(sender_id, conversation_id);
      return updated;
    });

    // Update the chat UI with the new message
    const newMessage = {
      text: content,
      type: "incoming",
      timestamp: createdAt || new Date().toISOString(),
    };

    // Update the chats state to include this new message
    setChats((prevChats) =>
      prevChats.map((chatGroup) => {
        const updatedUsers = chatGroup.users.map((chat) =>
          chat.userId === sender_id
            ? { ...chat, messages: [...chat.messages, newMessage] }
            : chat
        );
        return { ...chatGroup, users: updatedUsers };
      })
    );
  };

  // Handle message sent acknowledgments
  const handleMessageSentAck = (data) => {
    const { recipient_id, content, conversation_id, createdAt } = data;

    // Store the conversation ID for this recipient (in state and localStorage)
    setConversations((prev) => {
      const updated = { ...prev, [recipient_id]: conversation_id };
      // Store in localStorage
      storeConversation(recipient_id, conversation_id);
      return updated;
    });

    console.log(
      `Message sent successfully. Conversation ID: ${conversation_id}`
    );
  };

  // Handle user.message.broadcast.ack response
  const handleBroadcastMessageAck = (data) => {
    const { results } = data;

    // Store conversation IDs for each successful recipient
    if (results && Array.isArray(results)) {
      results.forEach((result) => {
        if (result.success && result.recipient_id && result.conversation_id) {
          setConversations((prev) => {
            const updated = {
              ...prev,
              [result.recipient_id]: result.conversation_id,
            };
            // Store in localStorage
            storeConversation(result.recipient_id, result.conversation_id);
            return updated;
          });
        }
      });
    }

    console.log("Broadcast message sent successfully");
  };

  // Handle conversation history responses
  const handleConversationHistory = (data) => {
    const { conversation_id, messages } = data;
    console.log("Received conversation history:", messages);

    // Find the user associated with this conversation
    const userId = Object.keys(conversations).find(
      (key) => conversations[key] === conversation_id
    );

    if (userId) {
      console.log("Found userId for conversation:", userId);

      // Format messages for the UI
      const formattedMessages = messages.map((msg) => ({
        text: msg.content,
        type: msg.sender_id === user?.data?.tenantId ? "outgoing" : "incoming",
        timestamp: msg.createdAt,
      }));

      // Update the chat with the history
      setChats((prevChats) =>
        prevChats.map((chatGroup) => {
          const updatedUsers = chatGroup.users.map((chat) => {
            // Match by userId instead of username for more reliable matching
            if (chat.userId === userId) {
              console.log("Updating messages for user:", chat.user);

              // Keep the initial welcome message if it exists
              let updatedMessages = [...formattedMessages];

              // Look for the welcome message that starts with "Hello! I'm a"
              const welcomeMessage = chat.messages.find(
                (msg) => msg.text && msg.text.includes("Hello! I'm a")
              );

              // If welcome message exists, add it at the beginning
              if (welcomeMessage) {
                updatedMessages = [welcomeMessage, ...updatedMessages];
              }

              console.log("Final message count:", updatedMessages.length);
              return { ...chat, messages: updatedMessages };
            }
            return chat;
          });
          return { ...chatGroup, users: updatedUsers };
        })
      );
    } else {
      console.log("Could not find user for conversation ID:", conversation_id);
    }
  };

  // Handle WebSocket errors
  const handleError = (errorData) => {
    console.error("WebSocket error:", errorData.message);
    setIsError(true);
    setErrorMessage(errorData.message || "An error occurred");
  };

  // Send a message via WebSocket
  const sendMessage = (recipientId, messageText) => {
    if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
      setIsError(true);
      setErrorMessage("Connection lost. Reconnecting...");
      connectWebSocket();
      return;
    }

    if (!isAuthenticated) {
      authenticateWebSocket();
      setIsError(true);
      setErrorMessage("Not authenticated. Please try again.");
      return;
    }

    const messagePayload = {
      msg_type: "user.message",
      msg_res: {
        message: "Sending message",
        data: {
          recipient_id: recipientId,
          message: messageText,
        },
      },
    };

    socketRef.current.send(JSON.stringify(messagePayload));
  };

  // Send initial message to create conversation
  const sendInitialMessage = (recipientId) => {
    const genericMessage = "Hello! I'm connecting to start a conversation.";
    sendMessage(recipientId, genericMessage);

    // Add a temporary outgoing message to the UI
    const newMessage = {
      text: genericMessage,
      type: "outgoing",
      timestamp: new Date().toISOString(),
    };

    setChats((prevChats) =>
      prevChats.map((chatGroup) => {
        const updatedUsers = chatGroup.users.map((chat) =>
          chat.userId === recipientId
            ? { ...chat, messages: [...chat.messages, newMessage] }
            : chat
        );
        return { ...chatGroup, users: updatedUsers };
      })
    );
  };

  // Send a broadcast message to multiple recipients
  const sendBroadcastMessage = (recipientIds, messageText) => {
    // console.log("reached sendBroadcastMessage",recipientIds, messageText)
    if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
      setIsError(true);
      setErrorMessage("Connection lost. Reconnecting...");
      connectWebSocket();
      return;
    }

    if (!isAuthenticated) {
      authenticateWebSocket();
      setIsError(true);
      setErrorMessage("Not authenticated. Please try again.");
      return;
    }

    const broadcastPayload = {
      msg_type: "user.message.broadcast",
      msg_res: {
        message: "Broadcasting message",
        data: {
          recipient_ids: recipientIds,
          message: messageText,
        },
      },
    };

    socketRef.current.send(JSON.stringify(broadcastPayload));
  };

  // Fetch conversation history
  const fetchConversationHistory = (conversationId) => {
    if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
      setIsError(true);
      setErrorMessage("Connection lost. Reconnecting...");
      connectWebSocket();
      return;
    }

    if (!isAuthenticated) {
      authenticateWebSocket();
      setIsError(true);
      setErrorMessage("Not authenticated. Please try again.");
      return;
    }

    const historyPayload = {
      msg_type: "conversation.history.request",
      msg_res: {
        message: "Requesting conversation history",
        data: {
          conversation_id: conversationId,
        },
      },
    };

    socketRef.current.send(JSON.stringify(historyPayload));
  };

  // Mark a conversation as read
  const markConversationAsRead = (conversationId) => {
    if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
      return;
    }

    if (!isAuthenticated) {
      return;
    }

    const markReadPayload = {
      msg_type: "conversation.markread.request",
      msg_res: {
        message: "Marking conversation as read",
        data: {
          conversation_id: conversationId,
        },
      },
    };

    socketRef.current.send(JSON.stringify(markReadPayload));
  };

  // Connect WebSocket when component mounts
  useEffect(() => {
    if (user?.data?.tenantId) {
      connectWebSocket();
    }

    return () => {
      if (socketRef.current) {
        socketRef.current.close();
        console.log("WebSocket disconnected due to component unmount.");
      }
    };
  }, [user, connectWebSocket]);

  // Input handling for the chat
  const handleInputChange = (event) => {
    setMessageInput(event.target.value);
  };

  // Send message function for the chat UI
  const handleSendMessage = () => {
    if (messageInput.trim() && activeChatId) {
      // Create the message object for UI update
      const newMessage = {
        text: messageInput,
        type: "outgoing",
        timestamp: new Date().toISOString(),
      };

      // Add message to UI
      const updatedChats = chats.map((chatGroup) => {
        const updatedUsers = chatGroup.users.map((chat) =>
          chat.userId === activeChatId
            ? { ...chat, messages: [...chat.messages, newMessage] }
            : chat
        );
        return { ...chatGroup, users: updatedUsers };
      });

      setChats(updatedChats);

      // Send message via WebSocket
      sendMessage(activeChatId, messageInput);

      setMessageInput("");
    }
  };

  // Handle mobile broadcast message
  const handleMobileBroadcast = () => {
    if (mobileRecipients.length > 0 && mobileMessageText.trim()) {
      sendBroadcastMessage(mobileRecipients, mobileMessageText);
      setMobileMessageText("");
      setMobileRecipients([]);
    }
  };

  // Handle Enter key press
  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      handleSendMessage();
    }
  };

  // Select a chat to display
  const handleChatSelect = (user, userId) => {
    setActiveChat(user);
    setActiveChatId(userId);

    // Check if we have a stored conversation ID for this user
    if (conversations[userId]) {
      fetchConversationHistory(conversations[userId]);
      markConversationAsRead(conversations[userId]);
    } else {
      // If no conversation exists, send an initial message to create one
      console.log(
        "No existing conversation found for this user. Sending initial message..."
      );
      console.log("User ID:", userId);
      sendInitialMessage(userId);
    }
  };

  // Switch between online chat and mobile messaging
  const handleChatTypeChange = (type) => {
    setActiveChatType(type);
    setActiveChat("");
  };

  // Find the current chat across all chat groups
  const findCurrentChat = () => {
    for (const chatGroup of chats) {
      const chat = chatGroup.users.find((chat) => chat.userId === activeChatId);
      if (chat) return chat;
    }
    return null;
  };

  const currentChat = findCurrentChat();

  return (
    <div className="messaging-app-pc-tenants">
      <div className="chat-container">
        <aside className="chat-sidebar">
          {/* <div className="chat-select-type">
            <div
              className={`chat-type ${
                activeChatType === "online" ? "selected" : ""
              }`}
              onClick={() => handleChatTypeChange("online")}
            >
              <IoChatbubbles size={25} /> Online chat
            </div>
            <div
              className={`chat-type ${
                activeChatType === "mobile" ? "selected" : ""
              }`}
              onClick={() => handleChatTypeChange("mobile")}
            >
              <TbDeviceMobileMessage size={25} />
              Mobile messages
            </div>
          </div> */}

          {activeChatType === "online" && (
            <OnlineChatList
              chats={chats}
              activeChat={activeChatId}
              handleChatSelect={handleChatSelect}
              getLastMessageSnippet={getLastMessageSnippet}
              getLastMessageTimestamp={getLastMessageTimestamp}
            />
          )}

          {activeChatType === "mobile" && (
            <div className="mobile-message-panel">
              <h3>SMS Broadcast</h3>
              <p>Select recipients and send messages to their mobile phones</p>
            </div>
          )}
        </aside>

        {activeChatType === "online" && (
          <>
            {activeChat === "" ? (
              <div className="no-chat-selected">
                Please select a chat to start messaging.
              </div>
            ) : (
              <OnlineChatMessages
                currentChat={currentChat}
                messageInput={messageInput}
                handleInputChange={handleInputChange}
                handleSendMessage={handleSendMessage}
                handleKeyPress={handleKeyPress}
                formatTimestamp={formatTimestamp}
                formatDate={formatDate}
              />
            )}
          </>
        )}

        {activeChatType === "mobile" && (
          <MobileChatMessages
            properties={properties}
            mobileMessageText={mobileMessageText}
            setMobileMessageText={setMobileMessageText}
            mobileRecipients={mobileRecipients}
            setMobileRecipients={setMobileRecipients}
            handleMobileBroadcast={handleMobileBroadcast}
            isLoading={isLoading}
            isError={isError}
            errorMessage={errorMessage}
            profile={profile}
          />
        )}
      </div>

      {isLoading && (
        <div className="loading-container">
          <div className="message">Authenticating ...</div>
          <div className="loader"></div>
        </div>
      )}

      {isError && (
        <div className="error-container">
          <div className="message">{errorMessage}</div>
        </div>
      )}
    </div>
  );
};

export default MessagingApp;
